import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Search, Filter, Clock, Bookmark, Download } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const History = () => {
  const { token } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [contractFilter, setContractFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [questionsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/questions/history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contracts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setQuestions(questionsRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (questionId, currentlySaved) => {
    try {
      if (currentlySaved) {
        await axios.delete(`${API}/questions/${questionId}/save`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/questions/${questionId}/save`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setQuestions(questions.map(q => 
        q.id === questionId ? { ...q, is_saved: !currentlySaved } : q
      ));
      toast.success(currentlySaved ? "Removed from saved" : "Answer saved");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleExport = async (questionId) => {
    try {
      // Get the token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      // Use fetch API for proper blob handling
      const response = await fetch(`${API}/questions/${questionId}/export-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Check response status
      if (!response.ok) {
        const errorText = await response.text();
        toast.error("Failed to download PDF");
        console.error("PDF export failed:", response.status, errorText);
        return;
      }

      // Validate Content-Type
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        const responseText = await response.text();
        toast.error("Server did not return a PDF file");
        console.error("Non-PDF response:", responseText);
        return;
      }

      // Convert to blob
      const blob = await response.blob();
      
      // Validate blob size
      if (blob.size < 1000) {
        toast.error("PDF file is too small or corrupted");
        return;
      }

      // Extract filename
      let filename = 'CBAIQ_Answer.pdf';
      const disposition = response.headers.get('content-disposition');
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success("PDF download started");
    } catch (error) {
      toast.error("Failed to download PDF. Please try again.");
      console.error("PDF export error:", error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getContractName = (contractId) => {
    const contract = contracts.find(c => c.id === contractId);
    return contract?.name || "Unknown Contract";
  };

  const getRiskClass = (level) => {
    const lower = level?.toLowerCase();
    if (lower === "low") return "risk-low";
    if (lower === "medium") return "risk-medium";
    if (lower === "high") return "risk-high";
    return "bg-secondary text-muted-foreground";
  };

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = !search || 
      q.question.toLowerCase().includes(search.toLowerCase());
    const matchesContract = contractFilter === "all" || 
      q.contract_id === contractFilter;
    return matchesSearch && matchesContract;
  });

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2" data-testid="history-title">
            Question History
          </h1>
          <p className="text-muted-foreground">
            Browse and search your previous questions
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search questions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-card"
              data-testid="search-input"
            />
          </div>
          <Select value={contractFilter} onValueChange={setContractFilter}>
            <SelectTrigger className="w-full sm:w-[200px] bg-card" data-testid="contract-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All contracts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Contracts</SelectItem>
              {contracts.map((contract) => (
                <SelectItem key={contract.id} value={contract.id}>
                  {contract.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Questions List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 skeleton" />
            ))}
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="empty-state bg-card border border-border rounded-md">
            <Clock className="empty-state-icon" />
            <h3 className="empty-state-title">No questions yet</h3>
            <p className="empty-state-description">
              {search || contractFilter !== "all" 
                ? "No questions match your filters" 
                : "Ask your first question to build your decision trail"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredQuestions.map((q) => (
              <div
                key={q.id}
                className="bg-card border border-border rounded-md overflow-hidden"
                data-testid={`history-item-${q.id}`}
              >
                {/* Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium mb-2">{q.question}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className={`risk-badge ${getRiskClass(q.risk_level)}`}>
                          {q.risk_level} Risk
                        </span>
                        <span>•</span>
                        <span>{getContractName(q.contract_id)}</span>
                        <span>•</span>
                        <span>{formatDate(q.created_at)}</span>
                        {q.is_saved && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-primary">
                              <Bookmark className="w-3 h-3 fill-current" /> Saved
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === q.id && (
                  <div className="border-t border-border p-4 bg-secondary/20">
                    {q.answer_sections.map((section, i) => (
                      <div key={`${q.id}-section-${section.title}-${i}`} className="mb-4 last:mb-0">
                        <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-1">
                          {section.title}
                        </h4>
                        <p className="text-sm whitespace-pre-line">{section.content}</p>
                      </div>
                    ))}
                    
                    {q.citations && q.citations.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">
                          Citations
                        </h4>
                        {q.citations.map((citation, i) => (
                          <div key={`${q.id}-cite-${i}`} className="citation-block citation-text mb-2">
                            {citation}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSave(q.id, q.is_saved);
                        }}
                        data-testid={`save-btn-${q.id}`}
                      >
                        <Bookmark className={`w-4 h-4 mr-1 ${q.is_saved ? "fill-current" : ""}`} />
                        {q.is_saved ? "Unsave" : "Save"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExport(q.id);
                        }}
                        data-testid={`export-btn-${q.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Export PDF
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default History;
